import React, { useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { Button } from './ui/button';
import { 
    Home, PlusCircle, BookOpen, Bell, Users, Settings, 
    Menu, X, LogOut, Brain, Heart, Crown, Sparkles, Zap, ShieldCheck, MoreHorizontal, Trophy
} from 'lucide-react';

// Admin email for dashboard access
const ADMIN_EMAIL = 'ybterrion@jcreppin.com';

const DashboardLayout = ({ children }) => {
    const { user, logout } = useAuth();
    const location = useLocation();
    const navigate = useNavigate();
    const [sidebarOpen, setSidebarOpen] = useState(false);

    const subscription = user?.subscription || { tier: 'basic' };
    const isBasic = subscription.tier === 'basic';
    const isAdmin = user?.email === ADMIN_EMAIL;
    const isChild = user?.account_type === 'child';

    // All navigation items
    const allNavItems = [
        { path: '/dashboard', icon: Home, label: 'Home' },
        { path: '/new-entry', icon: PlusCircle, label: 'New Entry' },
        { path: '/entries', icon: BookOpen, label: 'Entries' },
        { path: '/insights', icon: Brain, label: 'Insights' },
        { path: isChild ? '/rewards' : '/my-rewards', icon: Trophy, label: 'Rewards' },
        { path: '/reminders', icon: Bell, label: 'Reminders' },
        { path: '/partners', icon: Users, label: 'Partners' },
        { path: '/resources', icon: Heart, label: 'Resources' },
        { path: '/pricing', icon: Crown, label: 'Upgrade', highlight: isBasic },
        { path: '/settings', icon: Settings, label: 'Settings' },
    ];

    // Add admin link only for admin user
    if (isAdmin) {
        allNavItems.push({ path: '/admin', icon: ShieldCheck, label: 'Admin', isAdmin: true });
    }

    // Bottom nav items (main 4 + more menu)
    const bottomNavItems = [
        { path: '/dashboard', icon: Home, label: 'Home' },
        { path: '/new-entry', icon: PlusCircle, label: 'New' },
        { path: '/entries', icon: BookOpen, label: 'Entries' },
        { path: isChild ? '/rewards' : '/my-rewards', icon: Trophy, label: 'Rewards' },
    ];

    const handleLogout = () => {
        logout();
        navigate('/');
    };

    return (
        <div className="min-h-screen bg-background">
            <div className="noise-overlay" />
            
            {/* Mobile Header - With safe area for notch */}
            <header className="lg:hidden fixed top-0 left-0 right-0 z-40 bg-card/80 backdrop-blur-lg border-b border-border" style={{ paddingTop: 'env(safe-area-inset-top, 0px)' }}>
                <div className="flex items-center justify-center p-4">
                    <Link to="/dashboard" className="font-heading text-xl font-semibold flex items-center gap-2">
                        <img src="/icon-branded.png" alt="MoodTrack" className="w-7 h-7 rounded-lg" />
                        MoodTrack
                    </Link>
                </div>
            </header>

            {/* Desktop Sidebar */}
            <aside className={`
                fixed inset-y-0 left-0 z-50 w-72 bg-card/95 backdrop-blur-xl border-r border-border
                transform transition-transform duration-300 ease-in-out
                lg:translate-x-0 ${sidebarOpen ? 'translate-x-0' : '-translate-x-full'}
            `}>
                <div className="flex flex-col h-full">
                    {/* Logo */}
                    <div className="p-6 flex items-center justify-between">
                        <Link to="/dashboard" className="flex items-center gap-3">
                            <img src="/icon-branded.png" alt="MoodTrack" className="w-10 h-10 rounded-xl" />
                            <span className="font-heading text-xl font-semibold">MoodTrack</span>
                        </Link>
                        <button
                            onClick={() => setSidebarOpen(false)}
                            className="lg:hidden p-2 rounded-xl hover:bg-muted transition-colors"
                        >
                            <X className="w-5 h-5" />
                        </button>
                    </div>

                    {/* User Info & Streak */}
                    <div className="px-6 py-4 border-b border-border">
                        <div className="flex items-center gap-3">
                            <div className="w-10 h-10 bg-primary/10 rounded-full flex items-center justify-center">
                                <span className="font-semibold text-primary">
                                    {user?.name?.charAt(0).toUpperCase()}
                                </span>
                            </div>
                            <div className="min-w-0 flex-1">
                                <p className="font-medium truncate">{user?.name}</p>
                                <div className="flex items-center gap-2">
                                    <span className="text-xs text-muted-foreground truncate capitalize">
                                        {subscription.tier}
                                    </span>
                                    {user?.mood_streak > 0 && (
                                        <span className="flex items-center gap-1 text-xs text-chart-4">
                                            <Zap className="w-3 h-3" />
                                            {user.mood_streak}
                                        </span>
                                    )}
                                </div>
                            </div>
                        </div>
                    </div>

                    {/* Navigation */}
                    <nav className="flex-1 p-4 space-y-1 overflow-y-auto">
                        {allNavItems.map((item) => {
                            const isActive = location.pathname === item.path;
                            return (
                                <Link
                                    key={item.path}
                                    to={item.path}
                                    onClick={() => setSidebarOpen(false)}
                                    data-testid={`nav-${item.label.toLowerCase()}`}
                                    className={`
                                        flex items-center gap-3 px-4 py-3 rounded-xl transition-all duration-300
                                        ${isActive 
                                            ? 'bg-primary text-primary-foreground shadow-lg' 
                                            : item.highlight 
                                                ? 'text-primary bg-primary/5 hover:bg-primary/10'
                                                : item.isAdmin
                                                    ? 'text-amber-500 bg-amber-500/5 hover:bg-amber-500/10'
                                                    : 'text-muted-foreground hover:bg-muted hover:text-foreground'
                                        }
                                    `}
                                >
                                    <item.icon className="w-5 h-5" />
                                    <span className="font-medium">{item.label}</span>
                                    {item.highlight && !isActive && (
                                        <span className="ml-auto px-2 py-0.5 text-xs rounded-full bg-primary/20 text-primary">
                                            PRO
                                        </span>
                                    )}
                                    {item.isAdmin && !isActive && (
                                        <span className="ml-auto px-2 py-0.5 text-xs rounded-full bg-amber-500/20 text-amber-500">
                                            ADMIN
                                        </span>
                                    )}
                                </Link>
                            );
                        })}
                    </nav>

                    {/* Upgrade Banner for Basic Users */}
                    {isBasic && (
                        <div className="p-4">
                            <Link to="/pricing">
                                <div className="p-4 rounded-xl bg-gradient-to-br from-primary/10 to-accent/10 border border-primary/20">
                                    <div className="flex items-center gap-2 mb-2">
                                        <Crown className="w-4 h-4 text-primary" />
                                        <span className="text-sm font-medium">Upgrade to Plus</span>
                                    </div>
                                    <p className="text-xs text-muted-foreground">
                                        Unlock AI insights, unlimited history & more
                                    </p>
                                </div>
                            </Link>
                        </div>
                    )}

                    {/* Logout */}
                    <div className="p-4 border-t border-border">
                        <Button
                            variant="ghost"
                            onClick={handleLogout}
                            className="w-full justify-start gap-3 text-muted-foreground hover:text-destructive"
                            data-testid="sidebar-logout-btn"
                        >
                            <LogOut className="w-5 h-5" />
                            Sign Out
                        </Button>
                    </div>
                </div>
            </aside>

            {/* Mobile Sidebar Overlay */}
            {sidebarOpen && (
                <div
                    className="fixed inset-0 bg-black/50 z-40 lg:hidden"
                    onClick={() => setSidebarOpen(false)}
                />
            )}

            {/* Main Content - Added extra bottom padding for mobile nav + Emergent badge + safe area */}
            <main className="lg:ml-72 min-h-screen pb-36 lg:pt-0 lg:pb-0" style={{ paddingTop: 'calc(64px + env(safe-area-inset-top, 0px))' }}>
                <div className="p-4 sm:p-6 lg:p-8">
                    {children}
                </div>
            </main>

            {/* Mobile Bottom Navigation - Floating above Emergent badge */}
            <nav className="lg:hidden fixed z-40 bg-card backdrop-blur-xl border border-border shadow-xl rounded-2xl" style={{ bottom: '70px', left: '10px', right: '10px' }}>
                <div className="flex items-center justify-around px-1 py-3">
                    {bottomNavItems.map((item) => {
                        const isActive = location.pathname === item.path;
                        return (
                            <Link
                                key={item.path}
                                to={item.path}
                                data-testid={`bottom-nav-${item.label.toLowerCase()}`}
                                className={`
                                    flex flex-col items-center justify-center py-2 px-3 rounded-xl transition-all min-w-[55px]
                                    ${isActive 
                                        ? 'text-primary bg-primary/10' 
                                        : 'text-muted-foreground hover:text-foreground'
                                    }
                                `}
                            >
                                <item.icon className={`w-5 h-5 ${isActive ? 'scale-110' : ''} transition-transform`} />
                                <span className="text-[10px] mt-1 font-medium">{item.label}</span>
                            </Link>
                        );
                    })}
                    
                    {/* More Menu Button - Highlighted with purple background */}
                    <button
                        onClick={() => setSidebarOpen(true)}
                        data-testid="bottom-nav-more"
                        className="flex flex-col items-center justify-center py-2 px-3 rounded-xl bg-primary text-primary-foreground hover:bg-primary/90 transition-all min-w-[55px] shadow-md"
                    >
                        <Menu className="w-5 h-5" />
                        <span className="text-[10px] mt-1 font-medium">More</span>
                    </button>
                </div>
            </nav>
        </div>
    );
};

export default DashboardLayout;
